import React, { useState } from 'react';
import { SubmitHandler, useForm } from "react-hook-form";
import logo from "../images/preferenceslogo.png"
import background from "../images/preferencesbg.jpg"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod";
import SuccessfulSignup from './SuccessfulSignup';
import { useParams } from 'react-router-dom';

// Validation requirements for form fields using Zod
const schema = z.object({
  categories: z.string().array().nonempty({
    message: "Please select at least one category"
  }), 
})

type FormFields = z.infer<typeof schema>

const PreferencePage = () => {
  const { apiKey, formId } = useParams<string>();
  const [ formSubmitted, setFormSubmitted ] = useState(false);
  const { register, handleSubmit, setError, formState: { errors, isSubmitting } } = useForm<FormFields>({resolver: zodResolver(schema)});

  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    const formData = [
      {
        key: 1,
        value: data.categories.join(", "),
      }
  ];

    const dataFormatted = {
      Data: formData,
    };

    // Attempts to post data once form is submitted
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/preferences`, {
        method: "POST",
        headers: {
          'x-api-key': apiKey!,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataFormatted),
      });

      if (response.ok) {
        const text = await response.text();
        const result = text ? JSON.parse(text) : {};
        setFormSubmitted(true);
      } else {
        console.error("Failed to post data");
        setError("root", {
          message: "Error submitting form"
        })
      }
      
    } catch (error) {
      setError("root", {
        message: "Error submitting form",
      })
    }
  };

  return (
    <div className='flex justify-center items-center min-h-screen' 
    style={{
      backgroundImage: `url(${background})`,
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
      backgroundPosition: "center"
      }}>
      <div className='bg-white p-5 rounded-sm shadow-lg max-w-[95%] md:max-w-lg w-full'>
        {formSubmitted ? (
          <SuccessfulSignup message='Thank you for updating your preferences.'/> ) : 
          (
          <>
            <img src={logo} className='mb-5' alt="Logo"/>
            <h1 className='text-4xl text-chestertons-purple'>Update your preferences</h1>
            
            <form className="form flex flex-col" onSubmit={handleSubmit(onSubmit)}>
              
              <label className="mt-4 mb-2">Simply update your preferences here:<span className='text-red-500'>*</span></label>
              <div className="flex flex-col mb-4">
                <div className='flex items-center mb-3'>
                  <input {...register("categories")} type="checkbox" value ="AllUpdates" className="peer relative appearance-none w-4 h-4 border 
                             border-black cursor-pointer checked:bg-chestertons-purple" id="all-updates-option" />
                    <div className='flex flex-col'>
                        <label htmlFor="all-updates-option" className="ml-2">All Updates from Chestertons</label>
                        <p className='ml-2 text-xs '>Receive everything from market news to event invitations & special offers.</p>
                    </div>
                </div>
                <div>
                  <div className='flex items-center'>
                    <input {...register("categories")} type="checkbox" value="PropertyMatching" className="peer relative appearance-none w-4 h-4 border 
                             border-black cursor-pointer checked:bg-chestertons-purple" id="property-matching-option" />
                        <div className='flex flex-col'>
                             <label htmlFor="property-matching-option" className="ml-2">Property Matching</label>
                             <p className='ml-2 text-xs'>Property suggestions that meet your requirements</p>
                        </div>
                  </div>
                </div>

                {errors.categories && <div className='text-red-500'>Please select at least one category</div>}
              </div>
              
              <button disabled={isSubmitting} type='submit' className='bg-chestertons-purple text-white p-2 w-24 rounded-full'>
                {isSubmitting ? "Loading" : "Update"}
              </button>
              {errors.root && <div className='text-red-500 mt-2'>{errors.root.message}</div>}
            </form>
           </>)}
      </div>
    </div>
  )
}

export default PreferencePage;
